import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, LogOut, RefreshCw, Pencil, Trash2, Archive, ArchiveRestore,
  Mail, Printer, Ship, X, Loader2, ClipboardList,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "vcs_admin_token";
const MAGIC_BOAT = "https://customer-assets.emergentagent.com/job_magic-cruise-sydney/artifacts/q1j2aq6h_MAGIC%20CRUISES%20BOAT%20SYDNEY.png";
const DIRECTIONS_URL = "https://www.google.com/maps/search/?api=1&query=King+Street+Wharf+3+Sydney";

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
const errMsg = (e) => e?.response?.data?.detail || e?.message || "Something went wrong.";

const STATUS_OPTIONS = ["paid", "pending", "refunded", "cancelled"];

const Login = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-vivid-base px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm rounded-3xl border border-white/10 bg-vivid-surface p-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-vivid-magenta/20"><Lock className="h-5 w-5 text-vivid-magenta" /></div>
          <div>
            <h1 className="font-heading text-xl font-bold text-white">Admin Access</h1>
            <p className="text-xs text-white/50">Vivid Cruises Sydney bookings</p>
          </div>
        </div>
        <label className="block text-sm text-white/70">Password</label>
        <input
          type="password"
          data-testid="admin-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          className="mt-2 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 text-white outline-none focus:border-vivid-magenta"
          placeholder="Enter admin password"
        />
        <button
          type="submit"
          data-testid="admin-login-btn"
          disabled={loading || !password}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-vivid-magenta py-3 font-medium text-white transition hover:bg-vivid-magentaHover disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Log in"}
        </button>
      </form>
    </div>
  );
};

const EditModal = ({ booking, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: booking.name || "", phone: booking.phone || "", email: booking.email || "",
    cruise_date: booking.cruise_date || "", adults: booking.adults || 0,
    children: booking.children || 0, payment_status: booking.payment_status || "paid",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, adults: Number(form.adults), children: Number(form.children) };
      const { data } = await axios.put(`${API}/admin/bookings/${booking.id}`, payload, authHeaders());
      toast.success("Booking updated");
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, type = "text") => (
    <div>
      <label className="block text-xs text-white/60">{label}</label>
      <input type={type} data-testid={`edit-${key}`} value={form[key]} onChange={(e) => set(key, e.target.value)}
        className="mt-1 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-vivid-cyan" />
    </div>
  );

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 p-5" data-testid="edit-modal">
      <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-vivid-surface p-7">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-white">Edit booking</h2>
          <button onClick={onClose} data-testid="edit-close"><X className="h-5 w-5 text-white/60" /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {field("Client name", "name")}
          {field("Phone", "phone")}
          {field("Email", "email")}
          {field("Cruise date", "cruise_date")}
          {field("Adults", "adults", "number")}
          {field("Children", "children", "number")}
          <div className="col-span-2">
            <label className="block text-xs text-white/60">Payment status</label>
            <select data-testid="edit-payment_status" value={form.payment_status} onChange={(e) => set("payment_status", e.target.value)}
              className="mt-1 w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-vivid-cyan">
              {STATUS_OPTIONS.map((s) => <option key={s} value={s} className="bg-vivid-surface">{s}</option>)}
            </select>
          </div>
        </div>
        <p className="mt-4 text-xs text-white/40">Total recalculates automatically from ticket counts (Adults $49, Kids $35).</p>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-xl border border-white/15 px-5 py-2.5 text-sm text-white/80">Cancel</button>
          <button onClick={save} data-testid="edit-save-btn" disabled={saving}
            className="flex items-center gap-2 rounded-xl bg-vivid-magenta px-5 py-2.5 text-sm font-medium text-white disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

const printConfirmation = (b) => {
  const tickets = [
    b.adults ? `${b.adults} Adult${b.adults > 1 ? "s" : ""}` : "",
    b.children ? `${b.children} Child${b.children > 1 ? "ren" : ""}` : "",
  ].filter(Boolean).join(", ") || "-";
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Booking Confirmation</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0;font-family:Arial,Helvetica,sans-serif;}
    body{color:#111;padding:32px;max-width:760px;margin:0 auto;}
    .banner{width:100%;height:230px;object-fit:cover;border-radius:14px;}
    h1{font-size:26px;margin-top:20px;color:#b0006b;}
    .sub{font-size:14px;color:#555;letter-spacing:1px;text-transform:uppercase;margin-top:4px;}
    .badge{display:inline-block;margin-top:14px;background:#e8f8ec;color:#137a3a;font-weight:bold;padding:6px 14px;border-radius:20px;font-size:13px;}
    table{width:100%;border-collapse:collapse;margin-top:22px;}
    td{padding:11px 8px;border-bottom:1px solid #eee;font-size:15px;}
    td.l{color:#777;width:38%;}
    td.v{font-weight:bold;}
    .total{font-size:20px;color:#b0006b;}
    .foot{margin-top:26px;padding-top:18px;border-top:2px solid #b0006b;font-size:14px;line-height:1.7;}
    .phone{font-size:17px;font-weight:bold;color:#111;}
    @media print{body{padding:0;}}
  </style></head><body>
    <img class="banner" src="${MAGIC_BOAT}" alt="Magic Cruises vessel on Sydney Harbour during Vivid"/>
    <h1>Magic Cruises</h1>
    <div class="sub">Vivid Cruise Sydney Harbour</div>
    <span class="badge">Booking Confirmed</span>
    <table>
      <tr><td class="l">Guest name</td><td class="v">${b.name || "-"}</td></tr>
      <tr><td class="l">Email</td><td class="v">${b.email || "-"}</td></tr>
      <tr><td class="l">Phone</td><td class="v">${b.phone || "-"}</td></tr>
      <tr><td class="l">Cruise date and time</td><td class="v">${b.cruise_date || "-"}</td></tr>
      <tr><td class="l">Tickets</td><td class="v">${tickets}</td></tr>
      <tr><td class="l">Total paid</td><td class="v total">$${Number(b.amount).toFixed(2)} ${b.currency || "AUD"}</td></tr>
      <tr><td class="l">Departs</td><td class="v">King Street Wharf, Wharf 3<br/><a href="${DIRECTIONS_URL}" style="color:#b0006b;">Get directions (Google Maps)</a></td></tr>
      <tr><td class="l">Booking reference</td><td class="v">${b.session_id || b.id}</td></tr>
    </table>
    <div class="foot">
      Please arrive at least 20 minutes before departure. Bring a warm jacket and your camera for front row Vivid views of the Sydney Opera House and Harbour Bridge.<br/><br/>
      <span class="phone">Phone enquiries: 0402 308 337</span><br/>
      Magic Cruises &middot; Vivid Cruise Sydney Harbour &middot; King Street Wharf, Wharf 3, Sydney NSW
    </div>
    <script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) { toast.error("Please allow pop-ups to print the confirmation."); return; }
  w.document.write(html);
  w.document.close();
};

const CRUISE_MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
const cruiseDateKey = (s) => {
  const m = String(s).match(/(\d{1,2})\s+(\w{3})\w*\s+(\d{4})(?:.*?(\d{1,2}):(\d{2})\s*(am|pm))?/i);
  if (!m) return Number.MAX_SAFE_INTEGER;
  let [, d, mon, y, h, min, ap] = m;
  h = h ? parseInt(h, 10) : 0;
  if (ap && /pm/i.test(ap) && h < 12) h += 12;
  if (ap && /am/i.test(ap) && h === 12) h = 0;
  return new Date(+y, CRUISE_MONTHS[mon.toLowerCase()] ?? 0, +d, h, min ? +min : 0).getTime();
};

const printManifest = async (date) => {
  let rows;
  try {
    const { data } = await axios.get(`${API}/admin/bookings?archived=false`, authHeaders());
    rows = date === "__all__" ? data : data.filter((b) => b.cruise_date === date);
  } catch (e) {
    toast.error(errMsg(e));
    return;
  }
  if (!rows.length) { toast.error("No bookings found for that cruise date."); return; }

  const groupsMap = {};
  rows.forEach((r) => {
    const key = r.cruise_date || "Date not set";
    (groupsMap[key] = groupsMap[key] || []).push(r);
  });
  const groups = Object.keys(groupsMap).sort().map((k) => ({ date: k, rows: groupsMap[k].sort((a, b) => (a.name || "").localeCompare(b.name || "")) }));
  const generatedOn = new Date().toLocaleString("en-AU");

  const sections = groups.map((g) => {
    const totA = g.rows.reduce((s, r) => s + (r.adults || 0), 0);
    const totC = g.rows.reduce((s, r) => s + (r.children || 0), 0);
    const trs = g.rows.map((r, i) => `<tr>
        <td>${i + 1}</td>
        <td class="nm">${r.name || "-"}</td>
        <td>${r.phone || "-"}</td>
        <td>${r.email || "-"}</td>
        <td class="c">${r.adults || 0}</td>
        <td class="c">${r.children || 0}</td>
        <td class="c b">${(r.adults || 0) + (r.children || 0)}</td>
        <td class="c">$${Number(r.amount || 0).toFixed(2)}</td>
        <td class="ref">${r.session_id || r.id}</td>
      </tr>`).join("");
    return `<section>
      <h2>${g.date}</h2>
      <table>
        <thead><tr><th>#</th><th>Guest name</th><th>Phone</th><th>Email</th><th class="c">Ad</th><th class="c">Ch</th><th class="c">Guests</th><th class="c">Paid</th><th>Booking ref</th></tr></thead>
        <tbody>${trs}</tbody>
      </table>
      <p class="tot">${g.rows.length} booking${g.rows.length > 1 ? "s" : ""} &middot; ${totA} adult${totA === 1 ? "" : "s"} &middot; ${totC} child${totC === 1 ? "" : "ren"} &middot; <strong>${totA + totC} passengers on board</strong></p>
    </section>`;
  }).join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Passenger Manifest</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0;font-family:Arial,Helvetica,sans-serif;}
    body{color:#111;padding:28px;}
    .head{border-bottom:3px solid #b0006b;padding-bottom:12px;margin-bottom:8px;}
    .head h1{font-size:22px;color:#b0006b;}
    .head .s{font-size:13px;color:#555;margin-top:3px;}
    .gen{font-size:11px;color:#888;margin-bottom:18px;}
    section{margin-bottom:26px;page-break-inside:avoid;}
    h2{font-size:16px;margin-bottom:8px;color:#111;background:#f4e8f0;padding:8px 12px;border-radius:6px;}
    table{width:100%;border-collapse:collapse;}
    th,td{border:1px solid #ddd;padding:7px 8px;font-size:12px;text-align:left;}
    th{background:#faf5f8;color:#555;}
    td.c,th.c{text-align:center;}
    td.b{font-weight:bold;}
    td.nm{font-weight:bold;}
    td.ref{font-size:10px;color:#888;}
    .tot{margin-top:8px;font-size:13px;color:#333;}
    @media print{body{padding:6px;}}
  </style></head><body>
    <div class="head"><h1>Vivid Cruises Sydney &middot; Passenger Manifest</h1>
    <div class="s">${date === "__all__" ? "All cruise dates" : date} &middot; Departs King Street Wharf, Wharf 3</div></div>
    <div class="gen">Generated ${generatedOn}</div>
    ${sections}
    <script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
  </body></html>`;

  const w = window.open("", "_blank");
  if (!w) { toast.error("Please allow pop-ups to print the manifest."); return; }
  w.document.write(html);
  w.document.close();
};

const Dashboard = ({ onLogout }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [manifestDate, setManifestDate] = useState("__all__");
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState([]);

  const revenueRows = useMemo(
    () => [...revenue].sort((a, b) => cruiseDateKey(a.cruise_date) - cruiseDateKey(b.cruise_date)),
    [revenue]
  );

  const cruiseDates = useMemo(
    () => Array.from(new Set(bookings.map((b) => b.cruise_date).filter(Boolean))).sort((a, b) => cruiseDateKey(a) - cruiseDateKey(b)),
    [bookings]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, statsRes, revRes] = await Promise.all([
        axios.get(`${API}/admin/bookings?archived=${showArchived}`, authHeaders()),
        axios.get(`${API}/admin/stats`, authHeaders()),
        axios.get(`${API}/admin/revenue`, authHeaders()),
      ]);
      setBookings(list.data);
      setStats(statsRes.data);
      setRevenue(revRes.data);
    } catch (e) {
      if (e?.response?.status === 401) { onLogout(); return; }
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [showArchived, onLogout]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (id, fn, successMsg) => {
    setBusyId(id);
    try { await fn(); if (successMsg) toast.success(successMsg); await load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusyId(null); }
  };

  const archive = (b) => doAction(b.id, () => axios.post(`${API}/admin/bookings/${b.id}/archive`, {}, authHeaders()), b.archived ? "Restored" : "Archived");
  const del = (b) => { if (window.confirm(`Delete booking for ${b.name || b.email}? This cannot be undone.`)) doAction(b.id, () => axios.delete(`${API}/admin/bookings/${b.id}`, authHeaders()), "Booking deleted"); };
  const resend = (b) => doAction(b.id, async () => { const { data } = await axios.post(`${API}/admin/bookings/${b.id}/resend`, {}, authHeaders()); toast.success(`Confirmation sent to ${data.email}`); });

  return (
    <div className="min-h-screen bg-vivid-base px-5 py-8 sm:px-8" data-testid="admin-dashboard">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Ship className="h-7 w-7 text-vivid-magenta" />
            <div>
              <h1 className="font-heading text-2xl font-bold text-white">Bookings</h1>
              <p className="text-xs text-white/50">Vivid Cruises Sydney admin</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived"
              className={`rounded-full border px-4 py-2 text-sm ${showArchived ? "border-vivid-cyan text-vivid-cyan" : "border-white/15 text-white/70"}`}>
              {showArchived ? "Viewing archived" : "View archived"}
            </button>
            <button onClick={load} data-testid="refresh-btn" className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-white/70"><RefreshCw className="h-4 w-4" /> Refresh</button>
            <button onClick={onLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm text-white"><LogOut className="h-4 w-4" /> Log out</button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:max-w-2xl" data-testid="stats-cards">
          <div className="rounded-2xl border border-green-500/25 bg-green-500/5 p-5">
            <p className="text-xs uppercase tracking-wide text-green-400/80">Current bookings</p>
            <p className="mt-1 font-heading text-3xl font-bold text-white" data-testid="stat-active-total">${(stats?.active?.total ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}</p>
            <p className="text-sm text-white/50"><span data-testid="stat-active-count">{stats?.active?.count ?? 0}</span> paid sales</p>
          </div>
          <div className="rounded-2xl border border-white/15 bg-white/5 p-5">
            <p className="text-xs uppercase tracking-wide text-white/50">Archived bookings</p>
            <p className="mt-1 font-heading text-3xl font-bold text-white/80" data-testid="stat-archived-total">${(stats?.archived?.total ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}</p>
            <p className="text-sm text-white/50"><span data-testid="stat-archived-count">{stats?.archived?.count ?? 0}</span> paid sales</p>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="revenue-panel">
          <div className="mb-3 flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-vivid-magenta" />
            <span className="font-medium text-white">Takings by cruise date</span>
            <span className="text-xs text-white/40">(current bookings)</span>
          </div>
          {revenueRows.length === 0 ? (
            <p className="text-sm text-white/50">No current bookings yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-left text-sm">
                <thead className="text-white/50">
                  <tr><th className="py-2 pr-4 font-medium">Cruise date</th><th className="py-2 pr-4 font-medium">Sales</th><th className="py-2 pr-4 font-medium">Seats sold</th><th className="py-2 pr-4 font-medium">Remaining</th><th className="py-2 font-medium">Takings</th></tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {revenueRows.map((r) => (
                    <tr key={r.cruise_date} data-testid={`revenue-row`} className="text-white/80">
                      <td className="py-2 pr-4">{r.cruise_date}</td>
                      <td className="py-2 pr-4">{r.count}</td>
                      <td className="py-2 pr-4">{r.seats ?? 0} / {r.capacity ?? 100}</td>
                      <td className={`py-2 pr-4 font-medium ${(r.remaining ?? 100) <= 0 ? "text-red-400" : (r.remaining ?? 100) <= 40 ? "text-amber-400" : "text-vivid-cyan"}`}>
                        {(r.remaining ?? 100) <= 0 ? "Sold out" : r.remaining}
                      </td>
                      <td className="py-2 font-semibold text-white">${Number(r.total).toLocaleString("en-AU", { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-white/15 text-white">
                    <td className="py-2 pr-4 font-medium">Total</td>
                    <td className="py-2 pr-4 font-medium">{revenueRows.reduce((s, r) => s + r.count, 0)}</td>
                    <td className="py-2 pr-4 font-medium">{revenueRows.reduce((s, r) => s + (r.seats || 0), 0)}</td>
                    <td className="py-2 pr-4"></td>
                    <td className="py-2 font-bold text-vivid-cyan" data-testid="revenue-grand-total">${revenueRows.reduce((s, r) => s + Number(r.total), 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl border border-vivid-cyan/25 bg-vivid-cyan/5 p-4" data-testid="manifest-bar">
          <div className="flex items-center gap-2 text-white">
            <ClipboardList className="h-5 w-5 text-vivid-cyan" />
            <span className="font-medium">Passenger manifest</span>
          </div>
          <select
            data-testid="manifest-date-select"
            value={manifestDate}
            onChange={(e) => setManifestDate(e.target.value)}
            className="rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white outline-none focus:border-vivid-cyan"
          >
            <option value="__all__" className="bg-vivid-surface">All cruise dates</option>
            {cruiseDates.map((d) => <option key={d} value={d} className="bg-vivid-surface">{d}</option>)}
          </select>
          <button
            onClick={() => printManifest(manifestDate)}
            data-testid="print-manifest-btn"
            className="flex items-center gap-2 rounded-full bg-vivid-cyan px-4 py-2 text-sm font-medium text-vivid-base transition hover:brightness-110"
          >
            <Printer className="h-4 w-4" /> Print / export manifest
          </button>
          <span className="text-xs text-white/50">Lists every passenger on the selected cruise with totals.</span>
        </div>

        <div className="mt-5 overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="bg-white/5 text-white/60">
              <tr>
                {["Booked", "Client", "Contact", "Cruise", "Tickets", "Total", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-white/50"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></td></tr>
              ) : bookings.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-white/50">No {showArchived ? "archived " : ""}bookings yet.</td></tr>
              ) : bookings.map((b) => (
                <tr key={b.id} data-testid={`booking-row-${b.id}`} className="text-white/80">
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-white/50">{(b.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 font-medium text-white">{b.name || "-"}</td>
                  <td className="px-4 py-3 text-xs"><div>{b.email || "-"}</div><div className="text-white/50">{b.phone || ""}</div></td>
                  <td className="px-4 py-3 text-xs">{b.cruise_date || "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{b.adults}A {b.children}C</td>
                  <td className="px-4 py-3 whitespace-nowrap font-semibold text-white">${Number(b.amount).toFixed(0)}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs ${b.payment_status === "paid" ? "bg-green-500/15 text-green-400" : "bg-yellow-500/15 text-yellow-400"}`}>{b.payment_status}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <IconBtn title="Edit" testid={`edit-${b.id}`} onClick={() => setEditing(b)} disabled={busyId === b.id}><Pencil className="h-4 w-4" /></IconBtn>
                      <IconBtn title="Resend confirmation email" testid={`resend-${b.id}`} onClick={() => resend(b)} disabled={busyId === b.id}><Mail className="h-4 w-4" /></IconBtn>
                      <IconBtn title="Print confirmation" testid={`print-${b.id}`} onClick={() => printConfirmation(b)}><Printer className="h-4 w-4" /></IconBtn>
                      <IconBtn title={b.archived ? "Restore" : "Archive"} testid={`archive-${b.id}`} onClick={() => archive(b)} disabled={busyId === b.id}>{b.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}</IconBtn>
                      <IconBtn title="Delete" testid={`delete-${b.id}`} onClick={() => del(b)} disabled={busyId === b.id} danger><Trash2 className="h-4 w-4" /></IconBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <EditModal booking={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};

const IconBtn = ({ children, title, onClick, testid, disabled, danger }) => (
  <button title={title} data-testid={testid} onClick={onClick} disabled={disabled}
    className={`flex h-8 w-8 items-center justify-center rounded-lg border transition disabled:opacity-40 ${danger ? "border-red-500/30 text-red-400 hover:bg-red-500/10" : "border-white/15 text-white/70 hover:bg-white/10 hover:text-white"}`}>
    {children}
  </button>
);

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };
  useEffect(() => { document.title = "Admin | Vivid Cruises Sydney"; }, []);
  return authed ? <Dashboard onLogout={logout} /> : <Login onSuccess={() => setAuthed(true)} />;
}
